import React, { useEffect, useState, useRef } from 'react';
import { useComparison, Plan } from './ComparisonContext';
import { Check, X, ArrowLeft, MessageCircle, Send, Link as LinkIcon, ChevronDown } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HostingsAPI, OrdersAPI } from '../api';
import { useUser, useClerk } from '@clerk/clerk-react';
import { SafeHtml } from './SafeHtml';

const ExpandableCell = ({ value, detail }: { value: React.ReactNode, detail?: string | null }) => {
    const [isOpen, setIsOpen] = useState(false);

    if (!detail) return (
        <div className="w-full h-full flex items-center justify-center">
            {value}
        </div>
    );

    return (
        <div className="w-full flex flex-col items-center">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between gap-3 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors focus:outline-none w-full"
            >
                <span className="font-medium">{value}</span>
                <ChevronDown size={14} className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
                className={`grid transition-[grid-template-rows] duration-200 ease-out w-full ${isOpen ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0'}`}
            >
                <div className="overflow-hidden">
                    <SafeHtml
                        html={detail}
                        mode="rich"
                        className="text-xs text-left w-full bg-blue-50/50 dark:bg-slate-700/30 p-3 rounded-lg border border-blue-100 dark:border-slate-600 [&_ul]:list-disc [&_ul]:pl-4 [&_li]:mb-1"
                    />
                </div>
            </div>
        </div>
    )
}

const HostingComparison = () => {
    const { selectedPlans, setPlans } = useComparison();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    // Plan Selection State
    const [selectingId, setSelectingId] = useState<string | null>(null);

    const { isSignedIn, user } = useUser();
    const clerk = useClerk();

    const handleSelectPlan = async (plan: any) => {
        if (!isSignedIn) {
            clerk.openSignIn();
            return;
        }

        if (!user) return;

        setSelectingId(plan.id);

        try {
            const res = await OrdersAPI.create(
                user.id,
                plan.id,
                {
                    userEmail: user.primaryEmailAddress?.emailAddress || '',
                    userName: user.fullName || user.firstName || 'Usuario',
                    planName: plan.displayName,
                    planPrice: plan.price.toString(),
                    planCurrency: plan.currency,
                    planPeriod: plan.payTypeDisplay || (plan.payType === 'ANUAL' ? 'Anual' : 'Mensual')
                },
                undefined
            );
            if (res.success) {
                navigate('/dashboard');
            } else {
                console.error('Error selecting plan:', res.error);
                alert('Hubo un error al seleccionar el plan. Por favor intente nuevamente.');
            }
        } catch (error) {
            console.error('Error selecting plan:', error);
            alert('Hubo un error al seleccionar el plan. Por favor intente nuevamente.');
        } finally {
            setSelectingId(null);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Use ref to track selectedPlans without triggering effect re-runs
    const selectedPlansRef = useRef(selectedPlans);
    useEffect(() => {
        selectedPlansRef.current = selectedPlans;
    }, [selectedPlans]);

    // 1. Sync URL -> Context on Mount (if params exist and context empty or just re-verify)
    useEffect(() => {
        const fetchAndSetPlans = async () => {
            const compareParam = searchParams.get('compare');
            if (!compareParam) {
                setLoading(false);
                return;
            }

            // If we already have plans that match the URL, maybe we don't need to fetch?
            // But to be safe and handle "Share Link" scenario where context is empty:
            const slugs = compareParam.split('-vs-');

            // Check if current context already matches (optimization)
            const currentSlugs = selectedPlansRef.current.map(p => p.name).sort();
            const urlSlugsSorted = [...slugs].sort();
            if (currentSlugs.length === slugs.length && currentSlugs.every((val, index) => val === urlSlugsSorted[index])) {
                setLoading(false);
                return;
            }

            // Fetch all plans to find matches
            try {
                // We assume HostingsAPI.list() gets all active plans.
                // Ideally we would fetch by exact slug list if API supported it, but list is okay for usually small # of plans.
                const res = await HostingsAPI.list();
                if (res.success && res.data) {
                    const allPlans = res.data;
                    const matchedPlans = allPlans.filter((p: any) => slugs.includes(p.name));
                    if (matchedPlans.length > 0) {
                        setPlans(matchedPlans);
                    }
                }
            } catch (e) {
                console.error("Error fetching plans for comparison:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchAndSetPlans();
    }, [searchParams, setPlans]); // Intentionally not including selectedPlans to avoid loop, though logic handles it.

    // 2. Sync Context -> URL when selection changes
    useEffect(() => {
        if (loading) return; // Don't overwrite URL while loading initial state

        if (selectedPlans.length === 0) {
            // Remove param if empty
            if (searchParams.has('compare')) {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('compare');
                setSearchParams(newParams, { replace: true });
            }
            return;
        }

        const slugs = selectedPlans.map(p => p.name).join('-vs-');
        const currentParam = searchParams.get('compare');

        if (currentParam !== slugs) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('compare', slugs);
            setSearchParams(newParams, { replace: true });
        }
    }, [selectedPlans, loading, searchParams, setSearchParams]);

    const sortedPlans = [...selectedPlans].sort((a, b) => a.price - b.price);

    // Share URL generator
    const shareUrl = window.location.href;

    const handleCopy = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // If no plans selected, redirect or show message
    if (!loading && selectedPlans.length === 0) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center p-4 text-center">
                <h2 className="text-2xl font-bold mb-4 dark:text-gray-100">No hay planes seleccionados para comparar</h2>
                <p className="text-gray-600 dark:text-gray-300 mb-8">Selecciona al menos dos planes en la página principal para ver sus diferencias.</p>
                <button
                    onClick={() => navigate('/')}
                    className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition"
                >
                    Volver al Inicio
                </button>
            </div>
        );
    }

    // Helper for bandwidth percentage logic from HostingPlans (reused here visually if needed, but table row is simpler)
    // We'll just display the text value as per standard comparison tables.

    const renderValue = (value: any) => {
        if (typeof value === 'boolean') {
            return value ? <Check className="w-5 h-5 text-green-500 mx-auto" /> : <X className="w-5 h-5 text-red-400 mx-auto" />;
        }
        if (value === 'NONE') return <span className="text-red-400">No</span>;
        // Basic translations based on backend enums
        if (value === 'BASIC') return 'Básica';
        if (value === 'MEDIUM') return 'Media';
        if (value === 'AIDED') return 'Mejorada'; // Example if exists
        if (value === 'SI' || value === 'si') return <Check className="w-5 h-5 text-green-500 mx-auto" />;

        return value;
    };

    const rows: { label: string; key: keyof Plan | 'custom'; render?: (p: Plan) => React.ReactNode }[] = [
        { label: 'Precio', key: 'price', render: (p) => <span className="font-bold text-xl">{p.currency} {p.price}<span className="text-base font-normal text-gray-500">/{p.payType === 'ANUAL' ? 'año' : 'mes'}</span></span> },
        {
            label: 'Recursos',
            key: 'resources',
            render: (p) => (
                <ExpandableCell
                    value={p.resources === 'COMPARTIDOS' ? 'Compartidos' : 'Dedicados'}
                    detail={p.resourcesDetail}
                />
            )
        },
        {
            label: 'Elegir Datacenter',
            key: 'dataCenterSelectable',
            render: (p) => (
                <ExpandableCell
                    value={p.dataCenterSelectable ? <span className="text-blue-600 font-bold">Sí</span> : <span className="text-gray-500">No</span>}
                    detail={p.dataCenterSelectable ? p.dataCenterDetail : null}
                />
            )
        },
        { label: 'Sistema', key: 'hostingSystem' },
        { label: 'SSL', key: 'ssl', render: (p) => p.ssl ? 'Certificado SSL gratis' : 'No' },
        {
            label: 'Almacenamiento', key: 'spaceGb', render: (p) => (
                <div className="w-full">
                    <div className="flex justify-between text-sm mb-1">
                        <span className="font-bold">{p.spaceGb} GB</span>
                        <span className="text-gray-400 text-xs">de 50 GB</span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-1.5">
                        <div
                            className={`h-1.5 rounded-full ${(p.spaceGb / 50) * 100 < 25 ? 'bg-red-500' :
                                (p.spaceGb / 50) * 100 < 50 ? 'bg-orange-500' :
                                    (p.spaceGb / 50) * 100 < 80 ? 'bg-green-500' : 'bg-green-600'
                                }`}
                            style={{ width: `${Math.min((p.spaceGb / 50) * 100, 100)}%` }}
                        ></div>
                    </div>
                </div>
            )
        },
        {
            label: 'Bandwidth', key: 'bandwidth', render: (p) => (
                <div className="w-full">
                    <div className="flex justify-between text-sm mb-1">
                        <span className="font-bold">{p.bandwidth || 'Ilimitado'}</span>
                        <span className="text-gray-400 text-xs">de Max.</span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-green-500" style={{ width: '100%' }}></div>
                        {/* Simplified bandwidth bar as logic was complex to duplicate without helper. */}
                    </div>
                </div>
            )
        },
        { label: 'E-mails', key: 'emails' },
        {
            label: 'Respaldos',
            key: 'backups',
            render: (p) => (
                <ExpandableCell
                    value={p.backups ? 'Sí' : 'No'}
                    detail={p.backups ? p.backupsDetail : null}
                />
            )
        },
        {
            label: 'Seguridad', key: 'security', render: (p) => {
                const val = p.security === 'NONE' ? 10 : p.security === 'BASIC' ? 40 : p.security === 'MEDIUM' ? 70 : 100;
                const color = val < 20 ? 'bg-red-500' : val < 60 ? 'bg-orange-500' : 'bg-green-500';
                const text = p.security === 'NONE' ? 'No' : p.security === 'BASIC' ? 'Seguridad Básica' : p.security === 'MEDIUM' ? 'Seguridad Media' : 'Seguridad Mejorada';

                // For ExpandableCell, we pass the visual representation as 'value'
                const visual = (
                    <div className="w-full text-left">
                        <div className="font-semibold mb-1 text-center">{text}</div>
                        <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${val}%` }}></div>
                        </div>
                    </div>
                );

                return (
                    <ExpandableCell
                        value={visual}
                        detail={p.security !== 'NONE' ? p.securityDetail : null}
                    />
                )
            }
        },
        { label: 'Cache', key: 'cache', render: (p) => p.cache === 'NONE' ? 'No' : p.cache === 'SI' ? 'Sí' : p.cache === 'DINAMICA' ? 'Dinámica' : 'Dinámica y Objetos' },
        { label: 'Opt. de imágenes', key: 'imageOptimization', render: (p) => p.imageOptimization === 'NONE' ? 'no' : p.imageOptimization === 'SI' ? 'si' : 'Smush Pro' },
        {
            label: 'Elegir',
            key: 'custom',
            render: (p) => (
                <button
                    onClick={() => handleSelectPlan(p)}
                    disabled={selectingId === p.id}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {selectingId === p.id ? 'Procesando...' : 'Elegir plan'}
                </button>
            )
        }
    ];

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Desktop view - hidden on small screens */}
            <div className="hidden [@media(min-width:800px)]:block">
                <div className="mb-4 text-center">
                    <h1 className="text-3xl font-bold dark:text-gray-100">Encuentra el mejor hosting</h1>
                </div>

                <div className="relative flex items-center justify-center mb-8">
                    <button
                        onClick={() => navigate('/')}
                        className="absolute left-0 flex items-center gap-2 text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    >
                        <ArrowLeft size={20} />
                        Volver
                    </button>
                    <p className="text-gray-500">Puedes comparar hasta 4 hostings.</p>

                    {selectedPlans.length > 0 && (
                        <div className="absolute right-0 flex items-center gap-2">
                            <button
                                onClick={handleCopy}
                                className="bg-gray-600 hover:bg-gray-700 text-white p-1.5 rounded-lg transition-colors relative group"
                                title="Copiar enlace"
                            >
                                {copied ? <Check size={20} /> : <LinkIcon size={20} />}
                                {copied && (
                                    <span className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs py-1 px-2 rounded z-50 animate-in fade-in zoom-in duration-200 whitespace-nowrap">
                                        Copiado!
                                    </span>
                                )}
                            </button>
                            <a
                                href={`https://wa.me/?text=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-green-500 hover:bg-green-600 text-white p-1.5 rounded-lg transition-colors"
                                title="Compartir en WhatsApp"
                            >
                                <MessageCircle size={20} />
                            </a>
                            <a
                                href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded-lg transition-colors"
                                title="Compartir en Telegram"
                            >
                                <Send size={20} />
                            </a>
                        </div>
                    )}
                </div>

                {/* Comparison Grid */}
                <div className="overflow-x-auto shadow-sm border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800">
                    <div className="min-w-[800px] grid" style={{ gridTemplateColumns: `200px repeat(${sortedPlans.length}, 1fr)` }}>
                        {/* Header / Logos */}
                        <div className="p-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50"></div>
                        {sortedPlans.map(plan => (
                            <div key={plan.id} className="p-6 border-b border-l border-gray-100 dark:border-slate-700 text-center relative group">
                                <button
                                    onClick={() => {
                                        const newPlans = selectedPlans.filter(p => p.id !== plan.id);
                                        if (newPlans.length === 0) {
                                            navigate('/');
                                            return;
                                        }
                                        const slugs = newPlans.map(p => p.name).join('-vs-');
                                        window.location.href = `/comparar?compare=${slugs}`;
                                    }}
                                    className="absolute top-2 right-2 flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 hover:bg-red-500 hover:text-white text-gray-500 transition"
                                    title="Quitar"
                                >
                                    <X size={14} />
                                </button>
                                <div className="h-16 flex items-center justify-center mb-2">
                                    {plan.mainPhotoUrl ? (
                                        <img src={plan.mainPhotoUrl} alt={plan.displayName} className="h-full object-contain" />
                                    ) : (
                                        <div className="text-3xl font-bold text-gray-300">{plan.displayName.substring(0, 2)}</div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Rows */}
                        {rows.map((row, idx) => (
                            <React.Fragment key={idx}>
                                <div className="p-4 py-6 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 flex items-center">
                                    {row.label}
                                </div>
                                {sortedPlans.map(plan => (
                                    <div key={`${plan.id}-${idx}`} className="p-4 py-6 border-b border-l border-gray-100 dark:border-slate-700 flex items-center justify-center text-center text-sm text-gray-600 dark:text-gray-400">
                                        {row.render ? row.render(plan) : renderValue(plan[row.key as keyof Plan])}
                                    </div>
                                ))}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>

            {/* Mobile/Tablet view - shown on screens < 800px */}
            <div className="[@media(min-width:800px)]:hidden">
                {/* Title and subtitle */}
                <div className="mb-6 text-center">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Encuentra el mejor hosting</h1>
                    <p className="text-gray-500 dark:text-gray-400">Puedes comparar hasta 4 hostings.</p>
                </div>

                {/* Back button and Share buttons row */}
                <div className="mb-8 flex items-center justify-between">
                    {/* Volver button on the left */}
                    <button
                        onClick={() => navigate('/')}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                    >
                        <ArrowLeft size={20} />
                        Volver
                    </button>

                    {/* Share buttons on the right */}
                    {selectedPlans.length > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleCopy}
                                className="bg-gray-600 hover:bg-gray-700 text-white p-2.5 rounded-lg transition-colors relative"
                                title="Copiar enlace"
                            >
                                {copied ? <Check size={18} /> : <LinkIcon size={18} />}
                                {copied && (
                                    <span className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs py-1 px-2 rounded z-50 animate-in fade-in zoom-in duration-200 whitespace-nowrap">
                                        Copiado!
                                    </span>
                                )}
                            </button>
                            <a
                                href={`https://wa.me/?text=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-green-500 hover:bg-green-600 text-white p-2.5 rounded-lg transition-colors flex items-center justify-center"
                            >
                                <MessageCircle size={18} />
                            </a>
                            <a
                                href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-blue-500 hover:bg-blue-600 text-white p-2.5 rounded-lg transition-colors flex items-center justify-center"
                            >
                                <Send size={18} />
                            </a>
                        </div>
                    )}
                </div>

                {/* Accordion sections for each comparison row */}
                <div className="space-y-6">
                    {rows.map((row, idx) => (
                        <div key={idx} className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
                            <div className="px-4 py-3 font-semibold text-gray-900 dark:text-gray-100 border-b-2 border-blue-500">
                                {row.label}
                            </div>
                            <div className="divide-y divide-gray-100 dark:divide-slate-700">
                                {sortedPlans.map((plan) => (
                                    <div key={`${plan.id}-${idx}`} className="p-4 flex items-center gap-4">
                                        {/* Plan logo/icon */}
                                        <div className="flex-shrink-0 w-12 h-12 bg-gray-100 dark:bg-slate-700 rounded-lg flex items-center justify-center p-1">
                                            {plan.mainPhotoUrl ? (
                                                <img src={plan.mainPhotoUrl} alt={plan.displayName} className="w-full h-full object-contain" />
                                            ) : (
                                                <span className="font-bold text-sm text-gray-400">{plan.displayName.substring(0, 2)}</span>
                                            )}
                                        </div>
                                        {/* Plan value */}
                                        <div className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                                            {row.render ? row.render(plan) : renderValue(plan[row.key as keyof Plan])}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Back button at bottom */}
                <div className="mt-8 text-center">
                    <button
                        onClick={() => navigate('/')}
                        className="inline-flex items-center gap-2 px-6 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 rounded-full transition-colors"
                    >
                        <ArrowLeft size={18} />
                        Volver al inicio
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HostingComparison;
